import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, map, of, switchMap, take } from 'rxjs';
import { SnackbarService } from '@comparit/core';
import { TaskContextService } from '@comparit/shared';
import { prefillStammdaten } from '../helpers/kunde-stammdaten-prefill';
import { toWireSortErgebnis } from '../helpers/sort-ergebnis-wire';
import { Sparte } from '@comparit/common-api';
import { FormsService } from './forms.service';
import { KundeLoginDialogService, LoginDialogData } from './kunde-login-dialog.service';

type LoginOptions = Pick<LoginDialogData, 'mode' | 'reason'>;

/**
 * Mid-funnel login (header, step 1 banner). Delegates the dialog itself to
 * `KundeLoginDialogService` and layers on the concerns that need form state:
 * snapshotting the in-progress calculation for a guest who has no taskId yet,
 * and prefilling Stammdaten from the login response.
 *
 * Named for its scope: it is provided by `ShellComponent`, not the root
 * injector, so only the funnel can reach it. Injecting it constructs
 * `FormsService`, and a route that runs before the session is hydrated
 * (`/laden`) would then cache empty forms and overwrite the restored
 * calculation. Outside the funnel, depend on `KundeLoginDialogService`; it has
 * no path to form state.
 */
@Injectable()
export class FunnelLoginService {
  private readonly loginDialog = inject(KundeLoginDialogService);
  private readonly taskContext = inject(TaskContextService);
  private readonly formsService = inject(FormsService);
  private readonly snackbar = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  readonly loggingOut = signal(false);

  openLogin(): void {
    this.login({ mode: 'default' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  login(options: LoginOptions): Observable<boolean> {
    return this.dialogData(options).pipe(
      switchMap(data => this.loginDialog.open(data)),
      map(response => {
        if (!response) return false;
        prefillStammdaten(this.formsService.zurPersonFormCreator, response.stammdaten);
        this.snackbar.success('Du bist jetzt eingeloggt.');
        return true;
      })
    );
  }

  private dialogData(options: LoginOptions): Observable<LoginDialogData> {
    const taskId = this.taskContext.taskId();

    if (taskId) {
      return of({ ...options, taskId, taskToken: null, taskData: null });
    }

    // No task yet: send the whole current calculation so the backend can create
    // it and bind the customer in one call (KFZ-2397).
    return this.formsService.berechnungFormularValue$.pipe(
      take(1),
      map(formValue => ({
        ...options,
        taskId: null,
        taskToken: null,
        taskData: {
          sparte: Sparte.Kfz,
          berechnungsvorgaben: formValue,
          selectedResults: this.formsService.calculatorForm.form.fgCtrls.compare.value,
          agbAkzeptiert: this.formsService.acceptTermsAndConditions.value,
          sortErgebnis: toWireSortErgebnis(this.formsService.calculatorForm.ctrls.sortBy.value),
          sortDescending: this.formsService.calculatorForm.ctrls.sortDescending.value,
        },
      }))
    );
  }
}
